/**
 * Work Log Review group-by pill bar — task 159c §D7.
 *
 * Click a pill to add the dimension to the active group-by stack; click
 * again to remove it. Stack order is the click order (left → right);
 * drag-to-reorder is deferred per the parent task's "(optional)".
 */

import { Button } from '@/components/primitives/Button';
import { GROUP_BY_DIMENSIONS, type GroupByDimension } from '@/types/workLogReview';
import styles from './WorkLogReviewGroupByPills.module.css';

const DIMENSION_LABELS: Record<GroupByDimension, string> = {
  app: 'App',
  appFileName: 'App File',
  title: 'Title',
  rootDomain: 'Root Domain',
  site: 'Site',
  url: 'URL',
  employee: 'Employee',
  businessUnit: 'Business Unit',
  project: 'Project',
  client: 'Client',
  classificationSource: 'Source',
  classifiedByRule: 'Rule',
  day: 'Day',
  hour: 'Hour',
  weekday: 'Weekday',
};

interface WorkLogReviewGroupByPillsProps {
  value: readonly GroupByDimension[];
  onChange: (next: GroupByDimension[]) => void;
}

export function WorkLogReviewGroupByPills({ value, onChange }: WorkLogReviewGroupByPillsProps) {
  const toggle = (dim: GroupByDimension) => {
    const idx = value.indexOf(dim);
    if (idx >= 0) {
      const next = [...value];
      next.splice(idx, 1);
      onChange(next);
    } else {
      onChange([...value, dim]);
    }
  };

  const clear = () => onChange([]);

  return (
    <div className={styles.wrapper} data-testid="wlr-groupby-pills">
      <span className={styles.label}>Group by</span>
      <div className={styles.pills}>
        {GROUP_BY_DIMENSIONS.map((dim) => {
          const active = value.includes(dim);
          return (
            <Button
              key={dim}
              variant="chip"
              size="sm"
              onClick={() => toggle(dim)}
              aria-pressed={active}
              data-testid={`wlr-pill-${dim}`}
            >
              {DIMENSION_LABELS[dim]}
            </Button>
          );
        })}
        {value.length > 0 && (
          <Button variant="ghost" size="sm" onClick={clear} data-testid="wlr-pill-clear">
            Clear
          </Button>
        )}
      </div>
    </div>
  );
}
